import { AnalyticsInputWarningIcon } from "./AnalyticsInputWarningIcon";
import {
  inputRefusalWarnings,
  type BindingTarget,
  type BoundDocumentId,
  type FeedsReadState,
  type TestTableDocument,
  type TestTableSourceRow,
} from "../../lib/analyticsFeeds";

/**
 * What will stop this test table's analytics tests from running, said under the
 * table.
 *
 * A row-level control cannot carry any of it. Every fact here is about the table
 * — that *no row* fills a parameter, that *two rows* are flagged as its
 * analytics input, that the one flagged row is matched to *no sample* — and the
 * only place that reads as is the table the rows belong to. The list inside each
 * row's editor marks the unfilled parameters too, because a reader who has
 * already opened one should not have to close it to learn why.
 *
 * These are the input refusals a run reports at Generate: everything they need
 * is already on the page — the projection declares which parameters are
 * required, the bindings read says what fills each, and the session's own rows
 * carry the flag and the matches — so they are answered here rather than after a
 * run has been spent finding them.
 *
 * All but one of them. The partial-reach line describes a run that starts and
 * answers some of the samples, so it is marked as the warning it is rather than
 * as a refusal: an auditor who reads it as one goes looking for a run that never
 * failed.
 */
export function AnalyticsInputRefusalsNote({
  targets,
  documents,
  sourceRows,
  documentNames,
  feedsState,
}: {
  targets: readonly BindingTarget[];
  documents: readonly TestTableDocument[];
  sourceRows: readonly TestTableSourceRow[];
  documentNames: ReadonlyMap<BoundDocumentId, string>;
  feedsState: FeedsReadState;
}) {
  // The sentences below are claims about the server, so only the one state in
  // which the list *is* the server's answer licenses them. An empty `targets` is
  // what a read in flight and a failed read both look like, and "nothing fills
  // it" is not a thing to say about parameters that have not arrived; a stale
  // list can say it of a parameter the auditor has just filled, because it is
  // the snapshot from before their write. Silence costs nothing here — an
  // unfilled optional parameter already renders exactly the same way.
  //
  // Written as the one reading that licenses the claim rather than as the
  // readings that do not, so a reading added later is silent until someone
  // decides it may speak. `unasked` is silent for the strongest form of the
  // reason: it is not that these sentences might be behind the server, but that
  // no read of the server was ever made.
  if (feedsState !== "ready") return null;
  const warnings = inputRefusalWarnings({
    targets,
    documents,
    sourceRows,
    documentNames,
  });
  if (warnings.length === 0) return null;
  return (
    <div className="mt-2 flex flex-col gap-1 px-1 text-xs">
      {/* Colour on the line and not on the list, because the list is not one
          kind of news: a shortfall reads as a refusal when the two are stacked
          in the same red. */}
      {warnings.map((warning) => (
        <div
          key={warning.message}
          className={`flex items-start gap-2 ${
            warning.kind === "refusal" ? "text-red-700" : "text-amber-700"
          }`}
        >
          <AnalyticsInputWarningIcon kind={warning.kind} />
          <span>{warning.message}</span>
        </div>
      ))}
    </div>
  );
}
